import {ExtractMediaFileMetadata} from "../../storage/Media";
import {validateFileForUpload} from "../../utils/fileValidation";
import {getWorkflowUploadFiletype} from "./workflowUploadValidation";

const minimumFileCountMessage = "Upload at least two files from the same conversation.";
const invalidFileTypeMessage = "Please upload audio or video files.";
const missingAudioMessage = "Each file needs spoken audio so we can build a shared transcript.";
const durationWarningMessage = "These files look different in length. Make sure they're from the same session.";

const hasLikelyAudioOrVideoMime = (file) => {
    const mimeType = String(file?.type || "").toLowerCase();
    if (mimeType.startsWith("audio/") || mimeType.startsWith("video/")) {
        return true;
    }

    const fileName = String(file?.name || "").toLowerCase();
    return /\.(wav|mp3|aiff|aif|m4a|flac|ogg|opus|mp4|m4v|mov|webm)$/i.test(fileName);
};

const resolveDurationWarning = (entries) => {
    const durations = entries
        .map((entry) => Number(entry?.durationSec ?? 0))
        .filter((durationSec) => Number.isFinite(durationSec) && durationSec > 0);

    if (durations.length < 2) return "";

    const shortest = Math.min(...durations);
    const longest = Math.max(...durations);
    const difference = longest - shortest;

    if (shortest <= 0) return "";
    if (difference < 60) return "";
    if (longest < shortest * 1.5) return "";
    return durationWarningMessage;
};

export async function validateEditMultiSpeakerSelection(files) {
    const safeFiles = Array.from(files || []).filter(Boolean);
    if (safeFiles.length < 2) {
        return {
            selectedFiles: [],
            validationMessage: minimumFileCountMessage,
            warningMessage: "",
        };
    }

    const selectedFiles = [];
    for (const file of safeFiles) {
        if (!hasLikelyAudioOrVideoMime(file)) {
            return {
                selectedFiles: [],
                validationMessage: invalidFileTypeMessage,
                warningMessage: "",
            };
        }

        const validation = await validateFileForUpload(file);
        if (validation.mode !== "local_ok") {
            return {
                selectedFiles: [],
                validationMessage: invalidFileTypeMessage,
                warningMessage: "",
            };
        }

        const metadata = await ExtractMediaFileMetadata({
            file,
            fileType: getWorkflowUploadFiletype(file),
        });

        if (!metadata?.hasAudioStream) {
            return {
                selectedFiles: [],
                validationMessage: missingAudioMessage,
                warningMessage: "",
            };
        }

        selectedFiles.push({
            file,
            durationSec: Number(metadata?.durationSec ?? metadata?.duration ?? 0) || 0,
        });
    }

    return {
        selectedFiles,
        validationMessage: "",
        warningMessage: resolveDurationWarning(selectedFiles),
    };
}
